import { endOfQuarter, startOfQuarter, subQuarters } from "date-fns";

// Map a date_from/date_to pair back to a human period label. Used by
// the persistent scope bar on Overview and the Suggestions list — when
// the user has picked a Period preset (Today, Last 7 Days, ...), surface
// its name; otherwise default to "All Time" or "Custom range".
//
// NB: `inferPeriodLabel` deliberately does NOT try to recognise the
// this_quarter/last_quarter presets added below alongside `periodToDates`.
// A calendar quarter is ~90-92 days long, so its span collides with the
// existing "Last 90 Days" day-count check for every quarter (all land
// within +/-2 days of 90) — recognising it correctly would need
// calendar-boundary checks to run *before* the day-count loop, reordering
// the precedence this function already documents/tests for This
// Month-vs-This Year. Out of scope for the filter work below; the only
// consumer (`OverviewPrint.tsx`'s printed date-range caption) will show
// "Last 90 Days" or "Custom range" for a quarter-scoped print, same
// pre-existing category of cosmetic mislabeling as any other period whose
// day-span coincidentally matches a preset window.

const PRESET_PERIODS: { label: string; daysWindow: number }[] = [
  { label: "Today", daysWindow: 0 },
  { label: "Last 7 Days", daysWindow: 7 },
  { label: "Last 30 Days", daysWindow: 30 },
  { label: "Last 90 Days", daysWindow: 90 },
];

export function inferPeriodLabel(
  date_from?: string,
  date_to?: string,
): string {
  if (!date_from || !date_to) return "All Time";
  const from = new Date(date_from);
  const to = new Date(date_to);
  const days = Math.round((to.getTime() - from.getTime()) / 86400000);
  for (const p of PRESET_PERIODS) {
    if (Math.abs(p.daysWindow - days) <= 1) return p.label;
  }
  // This Month / This Year heuristics — typed period start usually maps
  // to the first day of the current month or year.
  const now = new Date();
  if (
    from.getFullYear() === now.getFullYear() &&
    from.getMonth() === now.getMonth() &&
    from.getDate() === 1
  )
    return "This Month";
  if (
    from.getFullYear() === now.getFullYear() &&
    from.getMonth() === 0 &&
    from.getDate() === 1
  )
    return "This Year";
  return "Custom range";
}

// Local-date YYYY-MM-DD formatter. The backend's date filter rejects a
// full ISO timestamp with milliseconds + Z (preflight 200, GET
// ERR_FAILED) — every branch below must format through this so bounds are
// interpreted inclusively in the backend's own timezone.
const ymd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Map a Period preset value (`SuggestionFilters`' `PERIOD_OPTIONS`) to the
 * date_from/date_to pair the backend's date filter accepts. Pure, with an
 * injectable `now` so it's deterministic under test — `SuggestionFilters`
 * calls it with the real clock (the default).
 *
 * Every "This X" preset runs from the start of the current window through
 * today, since the window isn't over yet. `last_quarter` is the one
 * exception: a fully-elapsed calendar quarter, so both bounds are fixed
 * instead of trailing off at `now`.
 */
export function periodToDates(
  period: string,
  now: Date = new Date(),
): { date_from?: string; date_to?: string } {
  if (!period) return {};
  const startOfDay = (d: Date) => {
    d.setHours(0, 0, 0, 0);
    return d;
  };
  let from: Date;
  let to: Date = now;
  switch (period) {
    case "today":
      from = startOfDay(new Date(now));
      break;
    case "7d":
      from = startOfDay(new Date(now));
      from.setDate(from.getDate() - 7);
      break;
    case "30d":
      from = startOfDay(new Date(now));
      from.setDate(from.getDate() - 30);
      break;
    case "90d":
      from = startOfDay(new Date(now));
      from.setDate(from.getDate() - 90);
      break;
    case "this_month":
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case "this_quarter":
      from = startOfQuarter(now);
      break;
    case "last_quarter": {
      const prevQuarter = subQuarters(now, 1);
      from = startOfQuarter(prevQuarter);
      to = endOfQuarter(prevQuarter);
      break;
    }
    case "this_year":
      from = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      return {};
  }
  return {
    date_from: ymd(from),
    date_to: ymd(to),
  };
}
